<template>
  <div class="m-5">
    <div>
      <h3 class="text-4xl">Nieuwe klanten uploaden via Excel</h3>
    </div>
    <div v-if="user.wocos.length > 1" class="my-3 pb-5">
      Gekozen woco:
      <DropDown class="my-1" />
    </div>

    <div>
      <div v-if="clients.length > 0" class="flex flex-col gap-2">
        <Alert :alerts="alertMessages" />

        <div>Totaal {{ clients.length }} addressen gevonden.</div>

        <div class="flex flex-row gap-2 items-center">
          <div>Actie:</div>
          <div>
            <select v-model="action" class="p-2">
              <option value="create">Nieuwe klanten aanmaken</option>
              <option value="update">Bestaande klanten updaten</option>
            </select>
          </div>
        </div>

        <div class="italic" v-if="action === 'update'">
          Bij het updaten van gegevens wordt gekeken of de klantnaam is veranderd. Is dit het geval,
          dan worden alle telefoonnummers en emailadressen vervangen door de adressen in de Excel.
          Is de klantnaam gelijk, dan worden de telefoonnummers en emailadressen samengevoegd.
        </div>

        <div class="flex flex-col items-start gap-2">
          <div class="bg-red-100 p-2 rounded" v-if="validation.filter((v) => !v.success).length">
            Er zijn {{ validation.filter((v) => !v.success).length }} adressen met validatiefouten
          </div>
          <div
            class="bg-yellow-100 p-2 rounded"
            v-if="action === 'create' && duplicates.some((d) => d.duplicate)"
          >
            Er zijn {{ duplicates.filter((d) => d.duplicate).length }} adressen die al bestaan, deze
            worden bij het importeren overgeslagen
          </div>
          <div
            class="bg-red-100 p-2 rounded"
            v-if="action === 'update' && duplicates.some((d) => !d.duplicate)"
          >
            Er zijn {{ duplicates.filter((d) => !d.duplicate).length }} adressen gevonden die nog
            niet bestaan
          </div>
          <div class="bg-red-100 p-2 rounded" v-if="incorrectAddresses.some((i) => i?.message)">
            Er zijn {{ incorrectAddresses.filter((i) => i?.message).length }} foutieve adressen
            gevonden
          </div>
        </div>

        <div v-if="!valid">
          Als bovenstaande problemen zijn opgelost kun je de gegevens importeren.
        </div>
        <div v-else>
          <e-button
            color="green"
            @click="startImport"
            :loading="importIsLoading"
            :disabled="importCompleted"
          >
            Gegevens importeren
          </e-button>
        </div>

        <div v-if="importResult?.length === clients.length">
          Import voltooid.
          <span v-if="action === 'update'">
            {{ changedCount }} van {{ clients.length }} adressen gewijzigd<template
              v-if="changedCount < clients.length"
            >, voor de overige adressen waren er geen wijzigingen</template>.
          </span>
        </div>
        <div v-else-if="importResult?.length > 0">
          {{ importResult.length }}/{{ clients.length }} voltooid.
        </div>

        <div v-if="importErrors.length" class="bg-red-100 p-2 rounded">
          <div>{{ importErrors.length }} adressen niet geïmporteerd:</div>
          <ul class="text-xs max-h-40 overflow-y-auto list-disc list-inside">
            <li v-for="(importError, index) in importErrors" :key="index">
              {{ importError.address }}: {{ importError.error }}
            </li>
          </ul>
        </div>

        <div class="mb-2">
          <button
            type="button"
            v-if="!details"
            @click="details = true"
            class="p-1 rounded bg-blue-300 text-white"
          >
            Bekijk details per adres
          </button>
          <button
            type="button"
            v-else
            @click="details = false"
            class="p-1 rounded bg-blue-300 text-white"
          >
            Verberg details per adres
          </button>
        </div>

        <ExcelImportTable
          v-if="details"
          :clients="clients"
          :validation="validation"
          :duplicates="duplicates"
          :incorrect-addresses="incorrectAddresses"
          :action="action"
          :result="importResult"
          :changes="importChanges"
        />
      </div>
      <div v-else>
        <Alert :alerts="alertMessages" class="float-right" />
        <div class="flex flex-col">
          <div>Kies een Excel bestand:</div>
          <DragAndDrop @fileChange="fileChange" />
        </div>
      </div>
    </div>

    <Modal v-if="isFetching">
      <div class="flex flex-col items-center gap-3">
        <Spinner />
        <div v-if="checkProgress" class="bg-white p-2 rounded">{{ checkProgress }}</div>
      </div>
    </Modal>
  </div>
</template>

<script setup>
import readXlsxFile from 'read-excel-file'

import Spinner from '@/components/shared/Spinner.vue'
import Modal from '@/components/Modal.vue'
import Alert from '@/components/Alert.vue'
import DropDown from '@/components/DropDown.vue'
import DragAndDrop from '@/components/DragAndDrop.vue'
import ClientFormatter from '@/common/ClientFormatter'
import * as requests from '@/common/requests'
import ExcelImportTable from '@/components/ExcelImportTable.vue'
import { ref, computed } from 'vue'
import store from '@/store'
import { z } from 'zod'
import EButton from '@/components/EButton.vue'
import { useMutation } from '@tanstack/vue-query'

const schema = z.object({
  household: z.object({
    street: z.string().min(3).max(200),
    house_number: z.string().regex(/^[a-zA-Z0-9-( )]+$/),
    city: z.string().min(3).max(200),
    postal_code: z.string().regex(/^[0-9]{4}[A-Z]{2}$/),
    household_type: z.enum(['residential', 'cvz']).nullable(),
  }),
  people: z
    .array(
      z.object({
        emails: z.array(
          z.object({
            address: z.string().email(),
          })
        ),
        phones: z.array(
          z.object({
            number_text: z.string().regex(/^[0-9+\- ]{8,13} ?[a-zA-Z-( )]{0,30}$/),
          })
        ),
      })
    )
    .optional(),
})

const { user } = store.state
const action = ref('create')
const isFetching = ref(false)
const duplicates = ref([])
const incorrectAddresses = ref([])
const alertMessages = ref([])
const clients = ref([])
const validation = computed(() =>
  clients.value.map((c) => {
    const result = schema.safeParse(c)
    return result
  })
)
const details = ref(false)
const importResult = ref(null)
const importChanges = ref([])
const importErrors = ref([])
const importCompleted = ref(false)
const checkProgress = ref('')

const changedCount = computed(() => importChanges.value.filter((c) => c?.length).length)

const valid = computed(() => {
  if (validation.value.filter((v) => !v.success).length !== 0) {
    return false
  }

  if (incorrectAddresses.value.filter((i) => i.message && i.level !== 'warning').length !== 0) {
    return false
  }

  if (action.value === 'create') {
    // bestaande adressen blokkeren de import niet, die worden overgeslagen
    return true
  } else {
    // update, everything should be duplicated
    return duplicates.value.filter((d) => !d.duplicate).length === 0
  }
})

function isExcelFile(file) {
  const excelFileExtensions = ['xlsx', 'xls']
  return excelFileExtensions.includes(file.name.split('.').pop())
}

function fileChange({ target }) {
  isFetching.value = true

  const file = target.files[0]
  if (isExcelFile(file)) {
    readFileData(file)
  } else {
    alertMessages.value = [
      { message: 'Zorg ervoor dat het ingediende bestand een excel bestand is', type: 'danger' },
    ]
    isFetching.value = false
  }
}

function toClient(data) {
  return new ClientFormatter(data).client
}

async function readFileData(file) {
  importResult.value = null
  importChanges.value = []
  importCompleted.value = false

  try {
    const rows = await readXlsxFile(file)

    const fixedRows = rows
      .map((row) => row.map((cell) => (cell ? cell.toString() : '')))
      .filter((r) => r[1] !== '' && r[2] !== '')
    // filter checks if the id, address, and client name are filled in
    // if they all empty - we assume there is nothing to upload from this line
    const header = fixedRows[0]
    const mandatoryColumns = ['id', 'address', 'postcode', 'city']

    if (!mandatoryColumns.every((column) => header.includes(column))) {
      alertMessages.value = [{ message: 'Ongeldig excel bestandsformaat', type: 'danger' }]
      console.log('invalid format', header)
      isFetching.value = false
      return
    }
    alertMessages.value = []

    const formattedClients = fixedRows.slice(1).map(toClient)
    clients.value = formattedClients

    const data = formattedClients.map((c) => ({
      address: `${c.household.street} ${c.household.house_number} ${c.household.postal_code}`,
      postal_code: c.household.postal_code,
      house_number: c.household.house_number,
      id_woco: c.household.id_woco,
    }))

    // Grote lijsten in delen controleren, anders loopt de check tegen een timeout aan
    async function checkDuplicates(data) {
      const chunkSize = 500
      const merged = []

      for (let i = 0; i < data.length; i += chunkSize) {
        const response = await fetch('/api/client/operation/check/', {
          method: 'POST',
          body: JSON.stringify({
            clients: data.slice(i, i + chunkSize),
            woco: store.state.selectedWoco,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        })

        if (!response.ok) {
          return { ok: false }
        }
        merged.push(...(await response.json()).data)
      }

      return { ok: true, data: merged }
    }

    const checkAddressData = formattedClients.map((c) => ({
      postal_code: c.household.postal_code,
      house_number: c.household.house_number,
      city: c.household.city,
      street: c.household.street,
    }))

    const [response, incorrectAddressesResponse] = await Promise.all([
      checkDuplicates(data),
      requests.checkAddresses(checkAddressData, (done, total) => {
        checkProgress.value = `Adressen gecontroleerd: ${done}/${total}`
      }),
    ])
    checkProgress.value = ''
    console.log('address check', response, incorrectAddressesResponse)

    if (incorrectAddressesResponse.data.length) {
      incorrectAddresses.value = incorrectAddressesResponse.data
    } else {
      incorrectAddresses.value = []
      alertMessages.value.unshift({
        message: 'Probleem met ophalen adres/postcode check',
        type: 'danger',
      })
    }

    if (!response.ok) {
      alertMessages.value.unshift({
        message: 'Probleem met ophalen van dubbele addressen',
        type: 'danger',
      })
      isFetching.value = false
      return
    }
    duplicates.value = response.data
    console.log(duplicates.value)

    isFetching.value = false
  } catch (err) {
    alertMessages.value.unshift({
      message: 'Probleem met het klaarzetten van adressen',
      type: 'danger',
    })
    console.error(err)
    isFetching.value = false
  }
}

function createPayload(context, data) {
  return {
    type: 'create',
    context,
    data,
  }
}

function removePayload(context, data) {
  return {
    type: 'remove',
    context,
    data,
  }
}

function updatePayload(context, data) {
  return {
    type: 'update',
    context,
    data,
  }
}

function createPerson(person) {
  const operations = []
  operations.push(createPayload('person', person))

  person.phones.forEach((phone) =>
    operations.push(createPayload('phone', { ...phone, person_name: person.name }))
  )
  person.emails.forEach((email) =>
    operations.push(createPayload('email', { ...email, person_name: person.name }))
  )

  return operations
}

function createClientsPayload(clients, duplicates) {
  console.log('createClientsPayload')
  return clients.map((client, index) => {
    let operations = []
    const { household, people } = client
    const address = `${household.street} ${household.house_number} ${household.postal_code}`

    // adres bestaat al: geen operaties, zodat een (her)upload niet stukloopt op duplicaten
    if (duplicates[index]?.duplicate) {
      return { address, operations, changes: [] }
    }

    // kolom leeg gelaten: nieuwe adressen krijgen 'residential'. Dit niet op het
    // client-object zelf zetten, dat blijft de bron voor de tabel en voor een
    // eventuele tweede ronde als 'update'
    const newHousehold = {
      ...household,
      household_type: household.household_type || 'residential',
    }

    operations.push(createPayload('household', newHousehold))
    operations.push(...people.flatMap((person) => createPerson(person)))

    return {
      address,
      operations,
      changes: ['Aangemaakt'],
    }
  })
}

function updateClientsPayload(clients, duplicates) {
  console.log('updateClientsPayload')
  return clients.map((client, index) => {
    let operations = []
    const changes = []
    const { household, people } = client
    const existing = duplicates[index]?.duplicate
    const person = people[0]
    const address = `${household.street} ${household.house_number} ${household.postal_code}`

    if (!existing) return { address, operations, changes }

    // op een kopie werken: het bestaande huishouden blijft de vergelijkingsbasis,
    // zodat een tweede poging na een mislukte batch dezelfde operaties oplevert
    const updatedHousehold = { ...existing.household }
    let householdChanged = false

    // update woco id if existing woco id is empty or different from current
    if (
      String(household.id_woco).trim() !== '' &&
      String(existing.household.id_woco).trim() !== String(household.id_woco).trim()
    ) {
      const previousIdWoco = String(existing.household.id_woco).trim()
      updatedHousehold.id_woco = household.id_woco
      householdChanged = true
      changes.push(`Woco ID: ${previousIdWoco || 'leeg'} → ${String(household.id_woco).trim()}`)
    }

    // type alleen bijwerken als het is ingevuld, anders zou een bestand zonder
    // type-kolom alle bestaande cvz-adressen terugzetten naar residential
    if (
      household.household_type &&
      household.household_type !== existing.household.household_type
    ) {
      changes.push(
        `Type: ${existing.household.household_type || 'leeg'} → ${household.household_type}`
      )
      updatedHousehold.household_type = household.household_type
      householdChanged = true
    }

    if (householdChanged) {
      operations.push(updatePayload('household', updatedHousehold))
    }

    // if person data is not set in the excel, skip those steps
    if (!person) return { address, operations, changes }

    const existingPerson = existing.people.find((p) => p.name === person.name)

    // if the person name exists -> add emails/phone numbers that do not exist yet
    if (existingPerson) {
      const newPhones = person.phones
        // remove existing phones from the list
        .filter((phone) => !existingPerson.phones.some((p) => p.number_text === phone.number_text))
      const newEmails = person.emails
        // remove existing emails from the list
        .filter((email) => !existingPerson.emails.some((e) => e.address === email.address))

      operations = operations.concat(
        // add the remaining as new phones for this existing person
        newPhones.map((phone) => createPayload('phone', { ...phone, person_id: existingPerson.id })),
        // add the remaining as new emails for this existing person
        newEmails.map((email) => createPayload('email', { ...email, person_id: existingPerson.id }))
      )

      if (newPhones.length) {
        changes.push(`Telefoon toegevoegd: ${newPhones.map((p) => p.number_text).join(', ')}`)
      }
      if (newEmails.length) {
        changes.push(`Email toegevoegd: ${newEmails.map((e) => e.address).join(', ')}`)
      }
    }

    // if the person name does not exist -> remove existing people, add person
    else {
      operations = operations.concat(
        // remove existing people
        ...existing.people.map((p) => removePayload('person', { person_id: p.id })),
        // add person with phone number
        ...createPerson(person)
      )
      changes.push(
        existing.people.length
          ? `Klant vervangen: ${existing.people.map((p) => p.name).join(', ')} → ${person.name}`
          : `Klant toegevoegd: ${person.name}`
      )
    }

    return { address, operations, changes }
  })
}

const batchSize = 50
function chunks(list, size) {
  const result = []
  for (let i = 0; i < list.length; i += size) {
    result.push(list.slice(i, i + size))
  }
  return result
}

const { mutateAsync, isLoading: importIsLoading } = useMutation({
  mutationFn: async (clients) => {
    const payload = {
      clients,
      woco: store.state.selectedWoco,
    }
    const result = await fetch('/api/client/operation/update/', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: {
        'Content-Type': 'application/json',
      },
    })

    if (result.ok) {
      return result.json()
    } else {
      throw await result.json()
    }
  },
  onSuccess: (data) => {
    importResult.value = (importResult.value || []).concat(data.data.clients)
    if (data.data.errors?.length) {
      importErrors.value = importErrors.value.concat(data.data.errors)
    }
  },
  onError: (data) => {
    console.error(data)
  },
})

async function startImport() {
  console.log(action.value)
  importResult.value = []
  importErrors.value = []

  let updates =
    action.value === 'update'
      ? updateClientsPayload(clients.value, duplicates.value)
      : createClientsPayload(clients.value, duplicates.value)

  importChanges.value = updates.map((u) => u.changes)

  // changes is alleen voor weergave, niet meesturen naar de backend
  const sets = chunks(
    updates.map(({ address, operations }) => ({ address, operations })),
    batchSize
  )
  for (const clients of sets) {
    console.log(clients)
    try {
      await mutateAsync(clients)
    } catch (err) {
      // een mislukte batch stopt de import niet meer, de rest gaat gewoon door
      importErrors.value = importErrors.value.concat(
        clients.map((client) => ({
          address: client.address,
          error: err?.message || 'Import mislukt',
        }))
      )
    }
  }

  importCompleted.value = true
}
</script>
